'use client'

import type { ProductSliderProps } from '@maestro/commerce-blocks/components/product-slider/product-slider.types'

import { useEffect, useRef, useState } from 'react'

import ProductSlider from '@maestro/commerce-blocks/components/product-slider/product-slider'
import ProductSliderSkeleton from '@maestro/commerce-blocks/components/product-slider/product-slider-skeleton'

/**
 * Defers hydration of a below-the-fold product slider until it is near the
 * viewport. Until then it renders the (lightweight) slider skeleton, so the
 * real ProductSlider — and its many client-side product cards — are NOT part of
 * the initial hydration commit. This moves that main-thread work (and the
 * skeleton→cards swap) off the critical load window into a scroll-triggered,
 * user-initiated moment.
 *
 * SSR-safe: the initial state is `false` on both server and first client render,
 * so the markup matches and no hydration mismatch occurs. Only use this for
 * below-the-fold sliders (recommendations / CMS product blocks), never for
 * above-the-fold content.
 */
export default function LazyProductSlider(props: ProductSliderProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (visible) {
      return
    }
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No IntersectionObserver support → render eagerly rather than never.
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      // Start mounting a bit before the slider enters the viewport so the real
      // content is ready by the time the user scrolls to it (avoids a flash).
      { rootMargin: '400px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible])

  return (
    <div ref={ref}>
      {visible ? (
        <ProductSlider {...props} />
      ) : (
        <ProductSliderSkeleton length={props.products.length} />
      )}
    </div>
  )
}
